import { Canvas, unmountComponentAtNode } from "@react-three/fiber";
import type { GLProps } from "@react-three/fiber";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { SRGBColorSpace, WebGLRenderer } from "three";
import type { SceneQuality } from "../../scene/SceneCanvas";

interface NavigatorPerformanceHints extends Navigator {
  readonly deviceMemory?: number;
  readonly connection?: { readonly saveData?: boolean };
}

function detectSceneQuality(): SceneQuality {
  if (typeof navigator === "undefined") {
    return "reduced";
  }

  const hints = navigator as NavigatorPerformanceHints;
  const requestsReducedData =
    hints.connection?.saveData === true ||
    window.matchMedia?.("(prefers-reduced-data: reduce)").matches === true;
  const hasLowMemory =
    hints.deviceMemory !== undefined && hints.deviceMemory < 4;
  const hasFewCores =
    hints.hardwareConcurrency !== undefined && hints.hardwareConcurrency < 4;

  return requestsReducedData || hasLowMemory || hasFewCores
    ? "reduced"
    : "full";
}

function hasWebGl2Api(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.WebGL2RenderingContext !== "undefined"
  );
}

export function DemoCanvas({
  ariaLabel,
  cameraFov = 44,
  cameraPosition,
  children,
  onFallbackNeeded,
  onQualityChange,
}: {
  readonly ariaLabel: string;
  readonly cameraFov?: number;
  readonly cameraPosition: readonly [number, number, number];
  readonly children: ReactNode;
  readonly onFallbackNeeded?: () => void;
  readonly onQualityChange?: (quality: SceneQuality) => void;
}) {
  const hasWebGl2 = useMemo(() => hasWebGl2Api(), []);
  const quality = useMemo(() => detectSceneQuality(), []);
  const [canvasFailed, setCanvasFailed] = useState(false);
  const fallbackNeeded = useRef(onFallbackNeeded);
  const failureReported = useRef(false);
  const qualityReported = useRef(false);
  const removeContextLossListener = useRef<() => void>(() => {});

  const reportFailure = useCallback(() => {
    if (failureReported.current) {
      return;
    }
    failureReported.current = true;
    setCanvasFailed(true);
    fallbackNeeded.current?.();
  }, []);
  const createRenderer: GLProps = useCallback(
    async (defaults) => {
      try {
        return new WebGLRenderer({
          ...defaults,
          alpha: false,
          antialias: quality === "full",
          powerPreference:
            quality === "full" ? "high-performance" : "low-power",
        });
      } catch {
        try {
          unmountComponentAtNode(defaults.canvas);
        } catch {
          // App-level unmount remains the cleanup boundary.
        }
        reportFailure();
        return new Promise<WebGLRenderer>(() => undefined);
      }
    },
    [quality, reportFailure],
  );
  const observeContextLoss = useCallback(
    (canvas: HTMLCanvasElement) => {
      removeContextLossListener.current();
      const onContextLost = (event: Event) => {
        event.preventDefault();
        removeContextLossListener.current();
        removeContextLossListener.current = () => {};
        reportFailure();
      };
      canvas.addEventListener("webglcontextlost", onContextLost, {
        once: true,
      });
      removeContextLossListener.current = () => {
        canvas.removeEventListener("webglcontextlost", onContextLost);
      };
    },
    [reportFailure],
  );
  const handleCreated = useCallback(
    ({ gl }: { readonly gl: WebGLRenderer }) => {
      try {
        if (gl.getContext().isContextLost()) {
          throw new Error("WebGL context was lost during scene creation");
        }
        gl.outputColorSpace = SRGBColorSpace;
        observeContextLoss(gl.domElement);
        if (!qualityReported.current) {
          qualityReported.current = true;
          onQualityChange?.(quality);
        }
      } catch {
        reportFailure();
      }
    },
    [observeContextLoss, onQualityChange, quality, reportFailure],
  );

  useEffect(() => {
    fallbackNeeded.current = onFallbackNeeded;
  }, [onFallbackNeeded]);

  useEffect(() => {
    if (!hasWebGl2) {
      reportFailure();
    }
  }, [hasWebGl2, reportFailure]);

  useEffect(
    () => () => {
      removeContextLossListener.current();
    },
    [],
  );

  if (!hasWebGl2 || canvasFailed) {
    return null;
  }

  return (
    <Canvas
      aria-label={ariaLabel}
      camera={{
        far: 80,
        fov: cameraFov,
        near: 0.1,
        position: cameraPosition,
      }}
      className="scene-canvas"
      dpr={quality === "full" ? [1, 1.5] : 1}
      gl={createRenderer}
      onCreated={handleCreated}
    >
      <Suspense fallback={null}>{children}</Suspense>
    </Canvas>
  );
}
